import { TValidator } from "../../../../wizard/types";
import { WIZARD_ERROR_MESSAGE } from "../../../../wizard/constants";
import { isPositiveNumber } from "../../../../../helpers";
import { DurationUnit } from "../../../../../types";
import { CourseHousingType } from "../../../types";
import { validateAmountAndType, validateOtherFeesGroup } from "../validators";

type CommonObject = { [key: string]: any };

const validator: TValidator = async ({ values, activeStepName }) => {
  const errors: CommonObject = {};
  const notifyMessages: string[] = [];

  const activeStepValues = values[activeStepName] ?? {};

  const housingTypes = values?.step2?.housingTypes || [];
  const isBoardingRequired = housingTypes.includes(CourseHousingType.RESIDENCE);
  const isHomestayRequired = housingTypes.includes(CourseHousingType.HOMESTAY);

  errors.mainOtherFees = [];
  errors.addedOtherFees = [];

  // Price -----------------------------------------------
  if (!activeStepValues.price?.trim()) {
    errors.price = "Price is required";
  } else if (!isPositiveNumber(activeStepValues.price)) {
    errors.price = "Price is not valid";
  } else if (activeStepValues.price.trim().split(".")[1]?.length > 2) {
    errors.price = "Max 2 digits allowed after decimal point";
  }

  // Currency -----------------------------------------------
  if (!activeStepValues.currency) {
    errors.currency = "Currency is required";
  }

  // Duration -----------------------------------------------
  if (!activeStepValues.priceDurationUnit) {
    errors.priceDurationUnit = "Duration period is required";
  }

  if (activeStepValues?.priceDurationUnit?.id !== DurationUnit.FULL) {
    if (!activeStepValues.priceDurationAmount?.trim()) {
      errors.priceDurationAmount = "Duration is required";
    } else if (!isPositiveNumber(activeStepValues.priceDurationAmount)) {
      errors.priceDurationAmount = "Duration is not valid";
    } else if (activeStepValues.priceDurationAmount >= 10000) {
      errors.priceDurationAmount = "Max 4 digits allowed";
    } else if (
      activeStepValues.priceDurationAmount.trim().split(".")[1]?.length > 2
    ) {
      errors.priceDurationAmount = "Max 2 digits allowed after decimal point";
    }
  }

  // Commission -----------------------------------------------
  if (!activeStepValues.commissionAmount?.trim()) {
    errors.commissionAmount = "Commission is required";
  } else if (!isPositiveNumber(activeStepValues.commissionAmount)) {
    errors.commissionAmount = "Commission is not valid";
  } else if (
    activeStepValues?.commissionType?.name === "Percent" &&
    +activeStepValues.commissionAmount > 100
  ) {
    errors.commissionAmount = "Enter less than 100";
  } else if (
    activeStepValues.commissionAmount.trim().split(".")[1]?.length > 2
  ) {
    errors.commissionAmount = "Max 2 digits allowed after decimal point";
  }

  if (!activeStepValues.commissionType) {
    errors.commissionType = "Type is required";
  }

  // Accommodation Fees -----------------------------------------------
  const homestayAmount = activeStepValues.homestayAmount?.trim();
  const residenceAmount = activeStepValues.residenceAmount?.trim();
  const insuranceAmount = activeStepValues.insuranceAmount?.trim();

  if (isHomestayRequired && !homestayAmount) {
    errors.homestayAmount = "Homestay is required";
  }

  if (homestayAmount && !isPositiveNumber(homestayAmount)) {
    errors.homestayAmount = "Homestay is not valid";
  } else if (homestayAmount && homestayAmount.split(".")[1]?.length > 2) {
    errors.homestayAmount = "Max 2 digits allowed after decimal point";
  }

  if (isBoardingRequired && !residenceAmount) {
    errors.residenceAmount = "Residence is required";
  }

  if (residenceAmount && !isPositiveNumber(residenceAmount)) {
    errors.residenceAmount = "Residence is not valid";
  } else if (residenceAmount && residenceAmount.split(".")[1]?.length > 2) {
    errors.residenceAmount = "Max 2 digits allowed after decimal point";
  }

  if (insuranceAmount && !isPositiveNumber(insuranceAmount)) {
    errors.insuranceAmount = "Insurance is not valid";
  } else if (insuranceAmount && insuranceAmount.split(".")[1]?.length > 2) {
    errors.insuranceAmount = "Max 2 digits allowed after decimal point";
  }

  // check other fee names duplications --------------------------------
  const allOtherFees = [
    ...(activeStepValues.mainOtherFees || []),
    ...(activeStepValues.addedOtherFees || []),
  ].filter((fee) => fee && (fee.name?.trim() || fee.amount?.trim()));

  const allOtherFeesNames = allOtherFees.reduce((names, fee) => {
    return fee?.name?.trim() ? [...names, fee.name.trim()] : names;
  }, []);

  if (new Set(allOtherFeesNames).size !== allOtherFeesNames.length) {
    const message = "There are other fees with the same name";
    errors.otherFees = message;
    notifyMessages.push(message);
  }

  // check if other fee has name, mark amount as required
  // check if other fee has amount , mark name as required
  validateOtherFeesGroup(
    activeStepValues.mainOtherFees,
    "mainOtherFees",
    errors,
  );
  validateOtherFeesGroup(
    activeStepValues.addedOtherFees,
    "addedOtherFees",
    errors,
  );

  // Calculation --------------------------------------------------
  validateAmountAndType(
    activeStepValues.priceDiscountAmount,
    "priceDiscountAmount",
    activeStepValues.priceDiscountType,
    "priceDiscountType",
    errors,
  );

  validateAmountAndType(
    activeStepValues.otherFeesDiscountAmount,
    "otherFeesDiscountAmount",
    activeStepValues.otherFeesDiscountType,
    "otherFeesDiscountType",
    errors,
  );

  if (
    errors.mainOtherFees.length > 0 ||
    errors.addedOtherFees.length > 0 ||
    Object.keys(errors).length - 2 > 0
  ) {
    throw {
      [WIZARD_ERROR_MESSAGE]: notifyMessages.join("\n\n"),
      [activeStepName]: errors,
    };
  }
};

export default validator;
